import React, { useState, useEffect, useMemo } from 'react'
import { ErrorNotification, NothingMessage, Texto, GraviButton, Vertical, Horizontal } from '@gravitate-js/excalibrr'
import { Col, Divider, Form, Input, Row, Space, Spin, message, Popover } from 'antd'
import { CheckOutlined, DeleteFilled, PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import { useRateManagement } from '@api/useFreight/useFreight'
import { useQueryClient } from '@tanstack/react-query'

export const FreightRegionsDrawer = ({ closeDrawer, sites }) => {
  const [creating, setCreating] = useState(false)
  const [saveDisabled, setSaveDisabled] = useState(true)
  const [freightRegions, setFreightRegions] = useState([])
  const {
    data: metadataOptions,
    isLoading: isLoadingMetadata,
    isRefetching,
    refetch,
  } = useMasterMetadata('ratebook_options')
  const { useCreateRegion, useDeleteRegion } = useRateManagement()
  const createRegion = useCreateRegion()
  const deleteRegion = useDeleteRegion()

  const queryClient = useQueryClient()

  // Calculate site counts for each region
  const regionSiteCounts = useMemo(() => {
    const counts = {}
    sites.forEach((site) => {
      if (site.freight_region_id) {
        counts[site.freight_region_id] = (counts[site.freight_region_id] || 0) + 1
      }
    })
    return counts
  }, [sites])

  // Load the freight regions from the metadata
  useEffect(() => {
    // get unique regions
    if (metadataOptions) {
      // Transform to match our expected format and include site counts
      const formattedRegions = metadataOptions.freight_regions.map((region) => ({
        region_id: region.id,
        name: region.name,
        site_count: regionSiteCounts[region.id] || 0,
      }))
      setFreightRegions(formattedRegions)
    }
  }, [metadataOptions, regionSiteCounts])

  const handleFreightRegionSave = async (value) => {
    try {
      // Create a new region using the API
      await createRegion.mutateAsync({ name: value.name })

      // Invalidate both metadata and site overview queries
      queryClient.invalidateQueries({ queryKey: ['metadata'] })
      queryClient.invalidateQueries({ queryKey: ['siteOverviewQuery'] })

      // Show success message
      message.success('Success! Freight Region added.')

      // Reset UI state
      setCreating(false)
      setSaveDisabled(true)
    } catch (error) {
      ErrorNotification(error)
    }
  }

  const handleDeleteFreightRegion = async (id) => {
    try {
      // Delete the region using the API
      await deleteRegion.mutateAsync({ region_id: id })

      // Invalidate both metadata and site overview queries
      queryClient.invalidateQueries({ queryKey: ['metadata'] })
      queryClient.invalidateQueries({ queryKey: ['siteOverviewQuery'] })

      // Show success message
      message.success('Success! Freight Region deleted.')
    } catch (error) {
      ErrorNotification(error)
    }
  }

  if (isLoadingMetadata || isRefetching) {
    return (
      <Row justify='center' align='middle' style={{ height: '100%' }}>
        <Spin size='large' />
      </Row>
    )
  }

  return (
    <Vertical style={{ height: '100%', display: 'flex', flexDirection: 'column' }} gap={12}>
      <Horizontal verticalCenter style={{ flexShrink: 0 }}>
        <Texto style={{ flex: 1 }} category='p2'>
          Freight Regions
        </Texto>
        {!creating && (
          <GraviButton theme2 icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            Add Region
          </GraviButton>
        )}
      </Horizontal>

      {creating && (
        <Form
          name='freight-region'
          wrapperCol={{ span: 12 }}
          initialValues={{ remember: true }}
          onFinish={handleFreightRegionSave}
          onFinishFailed={ErrorNotification}
          autoComplete='off'
          style={{ flexShrink: 0 }}
        >
          <Horizontal verticalCenter>
            <Vertical flex={3} gap={4}>
              <Form.Item name='name' className='mb-1'>
                <Input placeholder='Enter Region Name' onChange={() => setSaveDisabled(false)} />
              </Form.Item>
            </Vertical>

            <Col className='pr-5'>
              <GraviButton
                size='small'
                className='mr-2'
                onClick={() => {
                  setSaveDisabled(true)
                  setCreating(false)
                }}
                buttonText='Cancel'
              />

              <GraviButton
                htmlType='submit'
                size='small'
                theme2
                disabled={saveDisabled}
                icon={<CheckOutlined />}
                loading={createRegion.isLoading}
              />
            </Col>
          </Horizontal>
        </Form>
      )}

      <Vertical
        gap={12}
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: '8px', // Add some padding for the scrollbar
        }}
      >
        {freightRegions.length > 0
          ? freightRegions.map((region, index) => (
              <Horizontal
                borderRadius={8}
                className='px-3 py-2'
                key={region.region_id}
                bg='default'
                flex='0'
                style={{ overflow: 'visible' }}
              >
                <Vertical flex={3} gap={4}>
                  <Texto weight='bold'>{region.name}</Texto>
                  <Texto appearance='hint'>{region.site_count || '0'} sites</Texto>
                </Vertical>

                <Horizontal gap={4} verticalCenter>
                  <RenameForm region={region} />
                  {region.site_count === 0 && (
                    <GraviButton
                      className='delete-freight-region'
                      error
                      shape='square'
                      size='small'
                      icon={<DeleteFilled />}
                      onClick={() => handleDeleteFreightRegion(region.region_id)}
                      loading={deleteRegion.isLoading}
                    />
                  )}
                </Horizontal>
              </Horizontal>
            ))
          : !creating && <NothingMessage title='No Regions Found' className='freight-region-nothing' />}
      </Vertical>
    </Vertical>
  )
}

const RenameForm = ({ region }) => {
  const [newName, setNewName] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { useRenameRegion } = useRateManagement()
  const renameRegion = useRenameRegion()
  const [saveLoading, setSaveLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setNewName('')
    }
  }, [isOpen])

  const handleRenameRegion = async (newName) => {
    if (!newName.trim()) {
      message.error('Region name cannot be empty')
      return
    }
    try {
      setSaveLoading(true)
      const payload = { region_id: region.region_id, new_name: newName }

      await renameRegion.mutateAsync(payload)

      // Invalidate both metadata and site overview queries
      queryClient.invalidateQueries({ queryKey: ['metadata'] })
      queryClient.invalidateQueries({ queryKey: ['siteOverviewQuery'] })

      message.success('Region renamed successfully')
      setIsOpen(false)
    } catch (error) {
      console.error('Rename error:', error)
      ErrorNotification(error)
    } finally {
      setSaveLoading(false)
    }
  }

  const PopoverContent = (
    <Vertical className='px-2 py-1' gap={4}>
      <Input
        value={newName}
        placeholder='Enter New Name'
        onChange={(e) => {
          setNewName(e.target.value)
        }}
      />
      <Horizontal style={{ width: '100%' }} gap={4}>
        <GraviButton size='small' style={{ flex: 1 }} buttonText='Cancel' onClick={() => setIsOpen(false)} />
        <GraviButton
          size='small'
          style={{ flex: 1 }}
          theme2
          buttonText='Save'
          onClick={() => {
            handleRenameRegion(newName)
          }}
          loading={saveLoading}
          disabled={!newName.trim()}
        />
      </Horizontal>
    </Vertical>
  )
  return (
    <Popover open={isOpen} placement='bottomRight' content={PopoverContent} title='Rename Region'>
      <GraviButton icon={<EditOutlined />} size='small' onClick={() => setIsOpen(true)} />
    </Popover>
  )
}
